import { lazy, type ComponentType } from "react";

interface IModule {
  default: ComponentType;
}

/**
 * `React.lazy` s možností načíst modul předem a po načtení ho vykreslovat synchronně.
 *
 * AIDEV-NOTE: Důvod je Suspense throttle Reactu 19 (`FALLBACK_THROTTLE_MS` = 300 ms): obsah, který
 * se pozastavil, se odkryje nejdřív 300 ms po zobrazení fallbacku, i když chunk dorazí za 20 ms.
 * `lazy` se pozastaví při prvním renderu vždy, i když je modul už stažený — proto po `preload()`
 * vracíme načtenou komponentu přímo, bez `lazy`, a Suspense se vůbec nespustí. Před načtením
 * se obal chová jako dnes (`lazy` + `<Suspense>` z `lazyRoute`). Stránka vykreslená před načtením
 * se po něm neodmontuje: React při porovnání typu rozbalí `lazy` na jeho výsledek (hlídá test). [TD-476]
 */
export const lazyWithPreload = (loader: () => Promise<IModule>) => {
  let loaded: ComponentType | undefined;
  let loading: Promise<IModule> | undefined;
  let isSpeculative = false;

  // AIDEV-NOTE: Načtení zahájené přes `preload()` je spekulativní, dokud neskončí nebo se k němu nepřipojí
  // skutečný odběratel (vykreslení stránky). Příznak čte handler `vite:preloadError` v `index.tsx`: Vite tu událost
  // vysílá SYNCHRONNĚ při selhání importu, dřív než se zamítne promise, takže `.catch` na `preload()` reload
  // zastavit nestihne a příznak musí být znám předem. Bez něj by neúspěšné přednačtení na pozadí znovu
  // načetlo stránku, na které student zrovna pracuje. Zamítnutý promise se zapomene, ať další pokus
  // (skutečná navigace) zkusí loader znovu a vyhodnotí příznak znovu. [TD-476]
  const load = async (speculative: boolean) => {
    if (loading === undefined) {
      isSpeculative = speculative;
      loading = loader().then(
        module => {
          loaded = module.default;
          isSpeculative = false;
          return module;
        },
        (error: unknown) => {
          loading = undefined;
          isSpeculative = false;
          throw error;
        },
      );
    } else if (!speculative) {
      isSpeculative = false;
    }

    return await loading;
  };

  const Lazy = lazy(async () => await load(false));

  const Component = () => {
    const Impl = loaded ?? Lazy;

    return <Impl />;
  };

  return {
    Component,
    // `speculative: false` pro načtení, o které student žádá (start přímo na stránce): jeho selhání
    // má stránku znovu načíst jako dosud.
    preload: async ({ speculative = true } = {}) => {
      await load(speculative);
    },
    isPreloadSpeculative: () => isSpeculative,
  };
};
